import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Package, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { useCapabilities, useCreateProduct, type ProductModel } from "~/lib/products";

const SLUG_RE = /^[a-z][a-z0-9_-]{1,40}$/;
const MODELS: ReadonlyArray<{ value: ProductModel; label: string }> = [
    { value: "query", label: "Query (per-query quota)" },
    { value: "seat", label: "Seat (per named user)" },
    { value: "device", label: "Device (per deployed endpoint)" },
];

const modalStyle: React.CSSProperties = { padding: "var(--space-6)" };

export function CreateProductDialog(): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [slug, setSlug] = useState("");
    const [name, setName] = useState("");
    const [model, setModel] = useState<ProductModel>("query");
    const [vendor, setVendor] = useState("DataFlowX");
    const [description, setDescription] = useState("");
    const [icon, setIcon] = useState("");
    const [caps, setCaps] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    const capabilities = useCapabilities();
    const create = useCreateProduct();

    function reset(): void {
        setSlug("");
        setName("");
        setModel("query");
        setVendor("DataFlowX");
        setDescription("");
        setIcon("");
        setCaps([]);
        setError(null);
        create.reset();
    }

    function toggleCap(cap: string): void {
        setCaps((cur) => (cur.includes(cap) ? cur.filter((c) => c !== cap) : [...cur, cap]));
    }

    async function onSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setError(null);
        if (!SLUG_RE.test(slug)) {
            setError("Slug must start with a lowercase letter and contain only a-z, 0-9, _, - (2-41 chars).");
            return;
        }
        if (!name.trim()) {
            setError("Name required.");
            return;
        }
        try {
            const res = await create.mutateAsync({
                slug,
                name: name.trim(),
                model,
                vendor: vendor.trim() || undefined,
                description: description.trim(),
                icon: icon.trim() || undefined,
                capabilities: caps,
            });
            toast.success(`Product “${res.product.name}” created`);
            setOpen(false);
            reset();
        } catch (err) {
            if (err instanceof ApiError && err.status === 409) {
                setError("A product with that slug already exists.");
            } else {
                setError(err instanceof ApiError ? err.message : "Failed to create product.");
            }
        }
    }

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
            }}
        >
            <Dialog.Trigger asChild>
                <button type="button" className="btn btn--primary press-crush">
                    <Plus size={14} />
                    <span>New product</span>
                </button>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content dialog-content--md enter-up" style={modalStyle}>
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">
                                <span className="cluster-xs">
                                    <Package size={16} aria-hidden />
                                    <span>New product</span>
                                </span>
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>
                        <Dialog.Description className="text-text-muted text-12" style={{ marginTop: "var(--space-2)" }}>
                            Register a product that will consume IntelRoom data. Slug and metering model are
                            immutable after creation.
                        </Dialog.Description>

                        <form onSubmit={onSubmit} className="stack" style={{ marginTop: "var(--space-4)" }}>
                            <div className="cluster" style={{ gap: "var(--space-3)" }}>
                                <label className="stack-xs" style={{ flex: 1 }}>
                                    <span className="text-12 text-text-muted">Slug</span>
                                    <input
                                        className="input"
                                        value={slug}
                                        onChange={(e) => setSlug(e.target.value.toLowerCase())}
                                        placeholder="dfx-cloud"
                                        required
                                        pattern="[a-z][a-z0-9_-]{1,40}"
                                        autoFocus
                                    />
                                </label>
                                <label className="stack-xs" style={{ flex: 2 }}>
                                    <span className="text-12 text-text-muted">Name</span>
                                    <input
                                        className="input"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="DataFlowX Cloud"
                                        required
                                    />
                                </label>
                            </div>

                            <div className="cluster" style={{ gap: "var(--space-3)" }}>
                                <label className="stack-xs" style={{ flex: 1 }}>
                                    <span className="text-12 text-text-muted">Metering model</span>
                                    <select
                                        className="input"
                                        value={model}
                                        onChange={(e) => setModel(e.target.value as ProductModel)}
                                    >
                                        {MODELS.map((m) => (
                                            <option key={m.value} value={m.value}>
                                                {m.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="stack-xs" style={{ flex: 1 }}>
                                    <span className="text-12 text-text-muted">Vendor</span>
                                    <input
                                        className="input"
                                        value={vendor}
                                        onChange={(e) => setVendor(e.target.value)}
                                        placeholder="DataFlowX"
                                    />
                                </label>
                            </div>

                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">
                                    Icon <span className="text-text-subtle">(lucide-react name, optional)</span>
                                </span>
                                <input
                                    className="input"
                                    value={icon}
                                    onChange={(e) => setIcon(e.target.value)}
                                    placeholder="shield"
                                />
                            </label>

                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Description</span>
                                <textarea
                                    className="input"
                                    rows={2}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </label>

                            <div className="stack-xs">
                                <span className="text-12 text-text-muted">
                                    Initial capabilities <span className="text-text-subtle">(editable later)</span>
                                </span>
                                <div className="cluster-xs">
                                    {capabilities.data?.map((c) => {
                                        const active = caps.includes(c.slug);
                                        return (
                                            <button
                                                key={c.slug}
                                                type="button"
                                                onClick={() => toggleCap(c.slug)}
                                                className={`chip ${active ? "chip--accent" : ""}`}
                                                aria-pressed={active}
                                                style={{ cursor: "pointer", height: 24, opacity: active ? 1 : 0.6 }}
                                                title={c.description}
                                            >
                                                {c.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {error && (
                                <div
                                    className="chip chip--danger shake"
                                    style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                                    role="alert"
                                >
                                    {error}
                                </div>
                            )}

                            <button type="submit" className="btn btn--primary press-crush" disabled={create.isPending}>
                                {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                <span>{create.isPending ? "Creating…" : "Create product"}</span>
                            </button>
                        </form>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
